import * as React from "react";
import { useState } from "react";
import { NavigationItem } from "./NavigationItem";
import { useNavigation } from "@/hooks/useNavigation";
import { useTheme } from "@/providers/ThemeProvider";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useSecureImage } from "@/hooks/useSecureFile";
import { Box, Flex, Button } from "@radix-ui/themes";

export function AdminNavigation() {
  const {
    menuItems,
    activeSection,
    expandedMenu,
    activeSubItem,
    handleItemClick,
    handleSubItemClick,
  } = useNavigation();
  const { currentTenant } = useAuth();
  const { isDarkMode } = useTheme();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  const toggleSidebar = () => setIsSidebarExpanded(!isSidebarExpanded);

  // Secure image loading for tenant logos
  const { blobUrl: standardLogoUrl, loading: standardLogoLoading } =
    useSecureImage(currentTenant?.logo_standard?.id || null);
  const { blobUrl: squareLogoUrl, loading: squareLogoLoading } = useSecureImage(
    currentTenant?.logo_square?.id || null
  );

  const getTenantStandardLogo = () => {
    if (standardLogoUrl) {
      return standardLogoUrl;
    }
    return "/assets/anythink-icon-02.svg";
  };

  const getTenantSquareLogo = () => {
    if (squareLogoUrl) {
      return squareLogoUrl;
    }
    return "/assets/anythink-icon-03.svg";
  };

  return (
    <Box
      asChild
      style={{
        height: "100%",
        width: isSidebarExpanded ? "256px" : "64px",
        backgroundColor: "var(--sidebar-bg, var(--color-surface))",
        borderRight: "1px solid var(--gray-6)",
        transition: "all 300ms",
        position: "relative",
      }}
    >
      <nav>
        <Flex direction="column" style={{ height: "100%" }}>
          {/* Logo Section */}
          <Box
            p="4"
            style={{
              display: "flex",
              justifyContent: isSidebarExpanded ? "flex-start" : "center",
            }}
          >
            {isSidebarExpanded ? (
              standardLogoLoading && currentTenant?.logo_standard ? (
                <Box
                  style={{
                    width: "120px",
                    height: "32px",
                    backgroundColor: "var(--gray-6)",
                    borderRadius: "var(--radius-2)",
                    animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  }}
                />
              ) : (
                <img
                  src={getTenantStandardLogo()}
                  alt="Anythink"
                  width={120}
                  height={32}
                  style={{
                    maxWidth: "100%",
                    filter: isDarkMode ? "invert(1)" : "none",
                  }}
                />
              )
            ) : squareLogoLoading && currentTenant?.logo_square ? (
              <Box
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "var(--gray-6)",
                  borderRadius: "var(--radius-2)",
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                }}
              />
            ) : (
              <img
                src={getTenantSquareLogo()}
                alt="Anythink"
                width={24}
                height={24}
                style={{
                  maxWidth: "100%",
                  filter: isDarkMode ? "invert(1)" : "none",
                }}
              />
            )}
          </Box>

          {/* Toggle Button */}
          <Button
            variant="ghost"
            size="1"
            onClick={toggleSidebar}
            style={{
              position: "absolute",
              right: "-6px",
              top: "26px",
              backgroundColor: "var(--gray-2)",
              border: "1px solid var(--gray-6)",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              padding: "0",
              cursor: "pointer",
              boxShadow: "var(--shadow-2)",
            }}
            title={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {isSidebarExpanded ? (
              <ChevronLeft style={{ width: "12px", height: "12px" }} />
            ) : (
              <ChevronRight style={{ width: "12px", height: "12px" }} />
            )}
          </Button>

          {/* Navigation Items */}
          <Box
            px="2"
            style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {menuItems.map((item) => (
              <NavigationItem
                key={item.id}
                id={item.id.toString()}
                label={item.display_name}
                icon={item.icon}
                route={item.href}
                subItems={
                  item.items?.map((child) => ({
                    label: child.display_name,
                    route: child.href,
                  })) || []
                }
                isActive={
                  activeSection === item.id.toString() &&
                  !item.items?.some((child) => child.href === activeSubItem)
                }
                isExpanded={expandedMenu === item.id.toString()}
                isSidebarExpanded={isSidebarExpanded}
                onItemClick={handleItemClick}
                onSubItemClick={handleSubItemClick}
              />
            ))}
          </Box>

          {/* Footer */}
          {isSidebarExpanded && (
            <Flex px="4" py="2" align="center" gap="1">
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--gray-11)",
                  transform: "translateY(1px)",
                }}
              >
                powered by
              </span>
              <img
                src="/assets/anythink-icon-02.svg"
                alt="Anythink"
                width={60}
                height={18}
                style={{
                  filter: isDarkMode ? "invert(1)" : "none",
                }}
              />
            </Flex>
          )}
        </Flex>
      </nav>
    </Box>
  );
}
